import React, { useState, useEffect } from "react";
import "./MyProfile.scss";
import {
  getProfileApi,
  updateProfileApi,
  updatePasswordApi,
  getProfileSessionsApi,
  revokeProfileSessionApi,
  revokeAllProfileSessionsApi,
  getPresignedUploadUrlApi,
  updateProfileImageApi,
} from "../../../api/api";

const TABS = ["Profile", "Security", "Sessions"];

// ── helpers ───────────────────────────────────────────────────────────────────
function SectionTitle({ children }) {
  return <h3 className="mp-section-title">{children}</h3>;
}

function TabBar({ active, onChange }) {
  return (
    <div className="mp-tabbar">
      {TABS.map((t) => (
        <button
          key={t}
          className={`mp-tabbar__tab ${active === t ? "mp-tabbar__tab--active" : ""}`}
          onClick={() => onChange(t)}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

function getInitials(name = "") {
  return (
    name
      .split(" ")
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "?"
  );
}

// ── Profile tab ───────────────────────────────────────────────────────────────
function ProfileTab() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ fullName: "", mobile: "" });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    setLoading(true);
    getProfileApi()
      .then((res) => {
        const user = res.data?.user || {};
        setProfile(user);
        setForm({ fullName: user.fullName || "", mobile: user.mobile || "" });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (field, val) =>
    setForm((prev) => ({ ...prev, [field]: val }));

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const res = await updateProfileApi({
        fullName: form.fullName,
        mobile: form.mobile,
      });
      const updated = res.data?.user || {};
      setProfile((prev) => ({ ...prev, ...updated }));
      setForm((prev) => ({
        ...prev,
        fullName: updated.fullName || prev.fullName,
        mobile: updated.mobile || prev.mobile,
      }));
      setSaveSuccess(true);
      setEditing(false);
      setTimeout(() => setSaveSuccess(false), 2500);
    } catch (err) {
      setSaveError(
        err.response?.data?.message || "Failed to update profile. Try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleProfileImageUpload = async (e) => {
    try {
      const file = e.target.files[0];
      if (!file) return;
      setUploadingImage(true);

      const presignedRes = await getPresignedUploadUrlApi({
        fileName: file.name,
        mimeType: file.type,
        folder: "profile-images",
      });

      const { uploadUrl, objectKey, fileUrl } = presignedRes.data;

      await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });

      await updateProfileImageApi({ objectKey });

      setProfile((prev) => ({
        ...prev,
        profileImage: objectKey,
        profileImageUrl: fileUrl,
      }));
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Failed to upload profile image");
    } finally {
      setUploadingImage(false);
    }
  };

  if (loading) {
    return (
      <div className="mp-profile-tab">
        <div className="mp-avatar-card mp-skeleton-card">
          <div className="mp-skeleton mp-skeleton--circle" />
          <div style={{ flex: 1 }}>
            <div
              className="mp-skeleton mp-skeleton--line"
              style={{ width: 160, marginBottom: 8 }}
            />
            <div
              className="mp-skeleton mp-skeleton--line"
              style={{ width: 100 }}
            />
          </div>
        </div>
        <div className="mp-info-card">
          <div className="mp-form-grid">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="mp-field">
                <div
                  className="mp-skeleton mp-skeleton--line"
                  style={{ width: 80, marginBottom: 8 }}
                />
                <div
                  className="mp-skeleton mp-skeleton--line"
                  style={{ width: "100%" }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mp-profile-tab">
      {saveSuccess && (
        <div className="mp-success-banner">
          <span className="mp-banner-icon">✓</span>
          Profile updated successfully.
        </div>
      )}

      {/* Avatar card */}
      <div className="mp-avatar-card">
        <div className="mp-avatar-card__left">
          <div className="mp-avatar-wrap">
            <div className="mp-avatar-card__circle">
              {profile?.profileImageUrl ? (
                <img
                  src={profile.profileImageUrl}
                  alt="profile"
                  className="mp-avatar-card__img"
                />
              ) : (
                getInitials(profile?.fullName)
              )}
            </div>
            {uploadingImage && (
              <div className="mp-avatar-uploading">
                <span className="mp-spinner" />
              </div>
            )}
          </div>
          <div className="mp-avatar-card__info">
            <div className="mp-avatar-card__name">{form.fullName || "—"}</div>
            <div className="mp-avatar-card__role">
              {profile?.role?.name || "—"}
            </div>
            <span className="mp-role-badge">{profile?.role?.name || "—"}</span>
          </div>
        </div>
        <div className="mp-avatar-card__actions">
          <label
            className={`mp-btn mp-btn--outline-white mp-btn--sm ${uploadingImage ? "mp-btn--disabled" : ""}`}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            {uploadingImage ? "Uploading…" : "Upload Photo"}
            <input
              type="file"
              accept="image/*"
              hidden
              disabled={uploadingImage}
              onChange={handleProfileImageUpload}
            />
          </label>
          <button
            className="mp-btn mp-btn--outline-white mp-btn--sm"
            onClick={() => {
              setEditing(!editing);
              setSaveError(null);
            }}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            {editing ? "Cancel" : "Edit Profile"}
          </button>
        </div>
      </div>

      {/* Details card */}
      <div className="mp-info-card">
        <SectionTitle>Personal Information</SectionTitle>

        {saveError && (
          <div className="mp-error-banner">
            <span className="mp-banner-icon">⚠</span>
            {saveError}
          </div>
        )}

        <div className="mp-form-grid">
          <div className="mp-field">
            <label className="mp-field__label">Full Name</label>
            {editing ? (
              <input
                className="mp-field__input"
                value={form.fullName}
                onChange={(e) => handleChange("fullName", e.target.value)}
                placeholder="Enter full name"
              />
            ) : (
              <div className="mp-field__value">{form.fullName || "—"}</div>
            )}
          </div>

          <div className="mp-field">
            <label className="mp-field__label">Employee No.</label>
            <div className="mp-field__value mp-field__value--muted">
              {profile?.employeeNo || "—"}
            </div>
            <span className="mp-field__hint">
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ marginRight: 3 }}
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              Cannot be changed
            </span>
          </div>

          <div className="mp-field">
            <label className="mp-field__label">Email Address</label>
            <div className="mp-field__value mp-field__value--muted">
              {profile?.email || "—"}
            </div>
            <span className="mp-field__hint">
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ marginRight: 3 }}
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              Contact system admin to update email
            </span>
          </div>

          <div className="mp-field">
            <label className="mp-field__label">Mobile</label>
            {editing ? (
              <input
                className="mp-field__input"
                value={form.mobile}
                onChange={(e) => handleChange("mobile", e.target.value)}
                placeholder="+968XXXXXXXX"
              />
            ) : (
              <div className="mp-field__value">{form.mobile || "—"}</div>
            )}
          </div>

          <div className="mp-field">
            <label className="mp-field__label">Department</label>
            <div className="mp-field__value mp-field__value--muted">
              {profile?.department?.name || "—"}
            </div>
          </div>

          <div className="mp-field">
            <label className="mp-field__label">Status</label>
            <div>
              <span
                className={`mp-status-pill ${profile?.status === "ACTIVE" ? "mp-status-pill--on" : "mp-status-pill--off"}`}
              >
                <span className="mp-status-pill__dot" />
                {profile?.status || "—"}
              </span>
            </div>
          </div>
        </div>

        {editing && (
          <div className="mp-form-actions">
            <button
              className="mp-btn mp-btn--secondary"
              onClick={() => {
                setEditing(false);
                setSaveError(null);
                setForm({
                  fullName: profile?.fullName || "",
                  mobile: profile?.mobile || "",
                });
              }}
            >
              Cancel
            </button>
            <button
              className="mp-btn mp-btn--primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="mp-spinner mp-spinner--white" />
                  Saving…
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Security tab ──────────────────────────────────────────────────────────────
function SecurityTab() {
  const [pwForm, setPwForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirm: "",
  });
  const [pwVisible, setPwVisible] = useState({
    currentPassword: false,
    newPassword: false,
    confirm: false,
  });
  const [saving, setSaving] = useState(false);
  const [pwError, setPwError] = useState(null);
  const [pwSaved, setPwSaved] = useState(false);

  const handlePwChange = (field, val) =>
    setPwForm((prev) => ({ ...prev, [field]: val }));

  const toggleVisible = (field) =>
    setPwVisible((prev) => ({ ...prev, [field]: !prev[field] }));

  const handleSavePassword = async () => {
    setPwError(null);
    if (!pwForm.currentPassword || !pwForm.newPassword || !pwForm.confirm) {
      setPwError("All fields are required.");
      return;
    }
    if (pwForm.newPassword !== pwForm.confirm) {
      setPwError("New password and confirmation do not match.");
      return;
    }
    if (pwForm.newPassword.length < 10) {
      setPwError("New password must be at least 10 characters.");
      return;
    }

    setSaving(true);
    try {
      await updatePasswordApi({
        currentPassword: pwForm.currentPassword,
        newPassword: pwForm.newPassword,
      });
      setPwSaved(true);
      setPwForm({ currentPassword: "", newPassword: "", confirm: "" });
      setTimeout(() => setPwSaved(false), 3000);
    } catch (err) {
      const msg =
        err.response?.data?.message || "Failed to update password. Try again.";
      setPwError(msg);
    } finally {
      setSaving(false);
    }
  };

  const strengthLevel = () => {
    const pw = pwForm.newPassword;
    if (!pw) return 0;
    let score = 0;
    if (pw.length >= 10) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    return score;
  };

  const strength = strengthLevel();
  const strengthLabels = ["", "Weak", "Fair", "Good", "Strong"];
  const strengthMods = ["", "weak", "fair", "good", "strong"];

  return (
    <div className="mp-security-tab">
      <div className="mp-info-card">
        <div className="mp-card__header-row">
          <SectionTitle>Change Password</SectionTitle>
        </div>

        {pwSaved && (
          <div className="mp-success-banner">
            <span className="mp-banner-icon">✓</span>
            Password updated successfully.
          </div>
        )}
        {pwError && (
          <div className="mp-error-banner">
            <span className="mp-banner-icon">⚠</span>
            {pwError}
          </div>
        )}

        <div className="mp-form-grid mp-form-grid--single">
          {[
            { key: "currentPassword", label: "Current Password" },
            { key: "newPassword", label: "New Password" },
            { key: "confirm", label: "Confirm New Password" },
          ].map(({ key, label }) => (
            <div key={key} className="mp-field">
              <label className="mp-field__label">{label}</label>
              <div className="mp-field__pw-wrap">
                <input
                  className="mp-field__input"
                  type={pwVisible[key] ? "text" : "password"}
                  value={pwForm[key]}
                  onChange={(e) => handlePwChange(key, e.target.value)}
                  placeholder="••••••••••"
                />
                <button
                  className="mp-field__eye"
                  onClick={() => toggleVisible(key)}
                  type="button"
                  tabIndex={-1}
                >
                  {pwVisible[key] ? (
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>

        {pwForm.newPassword && (
          <div className="mp-strength">
            <div className="mp-strength__bars">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className={`mp-strength__bar ${i <= strength ? `mp-strength__bar--${strengthMods[strength]}` : ""}`}
                />
              ))}
            </div>
            <span
              className={`mp-strength__label mp-strength__label--${strengthMods[strength]}`}
            >
              {strengthLabels[strength]}
            </span>
          </div>
        )}

        <div className="mp-form-actions">
          <button
            className="mp-btn mp-btn--primary"
            onClick={handleSavePassword}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="mp-spinner mp-spinner--white" />
                Saving…
              </>
            ) : (
              "Update Password"
            )}
          </button>
        </div>

        <div className="mp-pw-rules">
          <div className="mp-pw-rules__title">Password requirements</div>
          <ul>
            <li
              className={
                pwForm.newPassword.length >= 10 ? "mp-pw-rules__met" : ""
              }
            >
              Minimum 10 characters
            </li>
            <li
              className={
                /[A-Z]/.test(pwForm.newPassword) &&
                /[0-9]/.test(pwForm.newPassword) &&
                /[^A-Za-z0-9]/.test(pwForm.newPassword)
                  ? "mp-pw-rules__met"
                  : ""
              }
            >
              Must include uppercase, number and special character
            </li>
            <li>Cannot reuse last 5 passwords</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// ── Sessions tab ──────────────────────────────────────────────────────────────
function SessionsTab() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [revokingAll, setRevokingAll] = useState(false);
  const [revokeError, setRevokeError] = useState(null);

  const fetchSessions = () => {
    setLoading(true);
    setFetchError(null);
    getProfileSessionsApi()
      .then((res) => setSessions(res.data?.sessions || []))
      .catch((err) =>
        setFetchError(
          err.response?.data?.message || "Failed to load sessions.",
        ),
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const handleRevoke = async (id) => {
    setRevoking(id);
    setRevokeError(null);
    try {
      await revokeProfileSessionApi(id);
      setSessions((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      setRevokeError(
        err.response?.data?.message || "Failed to revoke session.",
      );
    } finally {
      setRevoking(null);
    }
  };

  const handleRevokeAll = async () => {
    setRevokingAll(true);
    setRevokeError(null);
    try {
      await revokeAllProfileSessionsApi();
      setSessions((prev) => prev.filter((s) => s.current));
    } catch (err) {
      setRevokeError(
        err.response?.data?.message || "Failed to revoke all sessions.",
      );
    } finally {
      setRevokingAll(false);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isMobile = (name) =>
    name != null &&
    ["iphone", "mobile", "android"].some((k) => name.toLowerCase().includes(k));

  return (
    <div className="mp-sessions-tab">
      <div className="mp-info-card">
        <div className="mp-card__header-row">
          <div>
            <SectionTitle>Active Sessions</SectionTitle>
            <p className="mp-card__desc">
              These devices are currently signed into your account.
            </p>
          </div>
          <button
            className="mp-btn mp-btn--secondary mp-btn--sm mp-btn--danger-outline"
            onClick={handleRevokeAll}
            disabled={revokingAll || loading}
          >
            {revokingAll ? (
              <>
                <span className="mp-spinner" />
                Revoking…
              </>
            ) : (
              <>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14H6L5 6" />
                  <path d="M10 11v6" />
                  <path d="M14 11v6" />
                  <path d="M9 6V4h6v2" />
                </svg>
                Revoke All Others
              </>
            )}
          </button>
        </div>

        {revokeError && (
          <div className="mp-error-banner">
            <span className="mp-banner-icon">⚠</span>
            {revokeError}
          </div>
        )}

        {loading ? (
          <div className="mp-sessions-list">
            {[1, 2, 3].map((i) => (
              <div key={i} className="mp-session-item">
                <div
                  className="mp-skeleton mp-skeleton--circle"
                  style={{ width: 44, height: 44 }}
                />
                <div style={{ flex: 1 }}>
                  <div
                    className="mp-skeleton mp-skeleton--line"
                    style={{ width: 180, marginBottom: 8 }}
                  />
                  <div
                    className="mp-skeleton mp-skeleton--line"
                    style={{ width: 120 }}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : fetchError ? (
          <div className="mp-error-banner">
            <span className="mp-banner-icon">⚠</span>
            {fetchError}
            <button
              className="mp-btn mp-btn--ghost mp-btn--sm"
              onClick={fetchSessions}
              style={{ marginLeft: "auto" }}
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="mp-sessions-list">
            {sessions.length === 0 ? (
              <div className="mp-empty">
                <svg
                  width="36"
                  height="36"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ opacity: 0.3, marginBottom: 10 }}
                >
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                  <line x1="8" y1="21" x2="16" y2="21" />
                  <line x1="12" y1="17" x2="12" y2="21" />
                </svg>
                No active sessions found.
              </div>
            ) : (
              sessions.map((s) => (
                <div
                  key={s.id}
                  className={`mp-session-item ${s.current ? "mp-session-item--current" : ""}`}
                >
                  <div className="mp-session-item__icon">
                    {isMobile(s.deviceName) ? (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect
                          x="5"
                          y="2"
                          width="14"
                          height="20"
                          rx="2"
                          ry="2"
                        />
                        <line x1="12" y1="18" x2="12.01" y2="18" />
                      </svg>
                    ) : (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect
                          x="2"
                          y="3"
                          width="20"
                          height="14"
                          rx="2"
                          ry="2"
                        />
                        <line x1="8" y1="21" x2="16" y2="21" />
                        <line x1="12" y1="17" x2="12" y2="21" />
                      </svg>
                    )}
                  </div>
                  <div className="mp-session-item__info">
                    <div className="mp-session-item__device">
                      {s.deviceName || "Unknown Device"}
                      {s.current && (
                        <span className="mp-current-tag">Current</span>
                      )}
                    </div>
                    <div className="mp-session-item__meta">
                      <span>Created: {formatTime(s.createdAt)}</span>
                      {s.expiresAt && (
                        <span> · Expires: {formatTime(s.expiresAt)}</span>
                      )}
                    </div>
                  </div>
                  <div className="mp-session-item__action">
                    {s.current ? (
                      <span
                        className="mp-session-item__active-dot"
                        title="Active"
                      />
                    ) : (
                      <button
                        className="mp-btn mp-btn--secondary mp-btn--sm mp-btn--danger-outline"
                        onClick={() => handleRevoke(s.id)}
                        disabled={revoking === s.id}
                      >
                        {revoking === s.id ? (
                          <>
                            <span className="mp-spinner" />
                            Revoking…
                          </>
                        ) : (
                          "Revoke"
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
function MyProfile() {
  const [tab, setTab] = useState("Profile");

  return (
    <div className="sec-page mp-page">
      <div className="mp-page__inner">
        <div className="mp-page__header">
          <div>
            <h1 className="mp-page__title">My Profile</h1>
            <p className="mp-page__subtitle">
              Manage your profile, password and active sessions.
            </p>
          </div>
        </div>

        <div className="mp-main-card">
          <TabBar active={tab} onChange={setTab} />
          <div className="mp-tab-content">
            {tab === "Profile" && <ProfileTab />}
            {tab === "Security" && <SecurityTab />}
            {tab === "Sessions" && <SessionsTab />}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MyProfile;
